import { useCallback, useEffect } from "react";
import styled from "@emotion/styled";
import { fontFamilies } from "@leafygreen-ui/tokens";
import { Body } from "@leafygreen-ui/typography";
import { useParams } from "react-router-dom";
import Icon from "@evg-ui/lib/components/Icon";
import { fontSize, size } from "@evg-ui/lib/constants/tokens";
import { useToastContext } from "@evg-ui/lib/context/toast";
import { leaveBreadcrumb } from "@evg-ui/lib/utils/errorReporting";
import { SentryBreadcrumbTypes } from "@evg-ui/lib/utils/sentry/types";
import LoadingBar from "components/LoadingBar";
import { LogTypes } from "constants/enums";
import { slugs } from "constants/routes";
import { useLogContext } from "context/LogContext";
import { useLogDownloader } from "hooks";
import NotFound from "pages/404";
import { getBytesAsString } from "utils/string";
import { useResolveLogURLAndRenderingType } from "./useResolveLogURLAndRenderingType";

interface LoadingPageProps {
  logType: LogTypes;
}

// GraphQL Int values use the signed 32-bit range.
const MAX_GRAPHQL_INT = 2 ** 31 - 1;

const parseExecution = (execution?: string): string | undefined => {
  if (!execution || !/^\d+$/.test(execution)) {
    return undefined;
  }

  const parsedExecution = Number(execution);
  if (
    !Number.isSafeInteger(parsedExecution) ||
    parsedExecution > MAX_GRAPHQL_INT
  ) {
    return undefined;
  }

  return String(parsedExecution);
};

const LoadingPage: React.FC<LoadingPageProps> = ({ logType }) => {
  const {
    [slugs.execution]: unvalidatedExecution,
    [slugs.fileName]: fileName,
    [slugs.groupID]: groupID,
    [slugs.origin]: origin,
    [slugs.testID]: testID,
    [slugs.taskID]: taskID,
  } = useParams();
  const execution = parseExecution(unvalidatedExecution);
  const isExecutionValid = execution !== undefined;
  const dispatchToast = useToastContext();
  const { ingestLines, preferences, setLogMetadata } = useLogContext();
  const { excludeTimestamps } = preferences;
  const {
    downloadURL,
    failingCommand,
    htmlLogURL,
    jobLogsURL,
    loading: isLoadingEvergreen,
    logPath,
    logsToMerge,
    rawLogURL,
    renderingType,
  } = useResolveLogURLAndRenderingType({
    excludeTimestamps,
    execution,
    fileName,
    groupID,
    logType,
    origin,
    taskID,
    testID,
  });

  const onComplete = useCallback(
    (logs: string[]) => {
      if (!isExecutionValid) {
        return;
      }
      leaveBreadcrumb(
        "ingest-log-lines",
        { logType },
        SentryBreadcrumbTypes.UI,
      );
      setLogMetadata({
        execution,
        fileName,
        groupID,
        htmlLogURL,
        jobLogsURL,
        logPath,
        logType,
        logsToMerge,
        origin,
        rawLogURL,
        renderingType,
        taskID,
        testID,
      });
      ingestLines(logs, renderingType, failingCommand);
    },
    [
      execution,
      failingCommand,
      fileName,
      groupID,
      htmlLogURL,
      ingestLines,
      isExecutionValid,
      jobLogsURL,
      logPath,
      logType,
      logsToMerge,
      origin,
      rawLogURL,
      renderingType,
      setLogMetadata,
      taskID,
      testID,
    ],
  );

  const {
    error,
    fileSize,
    isLoading: isLoadingLog,
  } = useLogDownloader({
    logType,
    onComplete,
    url: downloadURL,
  });

  useEffect(() => {
    if (error) {
      dispatchToast.error(error);
    }
  }, [dispatchToast, error]);

  if (!isExecutionValid) {
    return (
      <Container>
        <NotFound />
      </Container>
    );
  }

  if (isLoadingLog || isLoadingEvergreen) {
    return (
      <Container>
        <LoadingBarContainer>
          <FlexRow>
            <LogoContainer>
              <AnimationWrapper>
                {/* @ts-expect-error: useStroke is not recognized as a valid prop */}
                <Icon glyph="ParsleyLogo" size={36} useStroke />
              </AnimationWrapper>
              <StyledBody>Downloading log...</StyledBody>
            </LogoContainer>
            <DownloadSize>{getBytesAsString(fileSize)}</DownloadSize>
          </FlexRow>
          <LoadingBar indeterminate />
        </LoadingBarContainer>
      </Container>
    );
  }
  if (error) {
    return (
      <Container>
        <NotFound />
      </Container>
    );
  }
  return null;
};

const LoadingBarContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  width: 40%;
  gap: ${size.s};
`;

const LogoContainer = styled.div`
  display: flex;
  gap: ${size.s};
  align-items: flex-end;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const DownloadSize = styled.div`
  font-family: ${fontFamilies.code};
`;

const AnimationWrapper = styled.div`
  animation: sway 3s infinite ease-in-out;
  transform-origin: bottom;
  @keyframes sway {
    0% {
      transform: rotateZ(0deg);
    }
    25% {
      transform: rotateZ(-5deg);
    }
    50% {
      transform: rotateZ(5deg);
    }
    75% {
      transform: rotateZ(-5deg);
    }
    100% {
      transform: rotateZ(0deg);
    }
  }
`;

const StyledBody = styled(Body)`
  font-size: ${fontSize.l};
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

export default LoadingPage;
